import { useCallback, useEffect, useRef, useState } from 'react';
import { errorMessage } from '../lib/api/index.js';

interface QueryState<T> {
  /** Which request this state answers, so "loading" can be derived, not stored. */
  key: string | null;
  data: T | null;
  error: string | null;
}

/**
 * Loads data for an admin page and reloads when `key` changes or on demand.
 *
 * `loading` is DERIVED - "the last answer is for a different request" - rather
 * than set at the start of the effect, which keeps the effect free of the
 * synchronous state update the `set-state-in-effect` rule forbids. The previous
 * data stays on screen while the next page loads, so tables do not flash empty.
 * A superseded request is aborted, so a slow answer cannot land late.
 */
export function useAdminQuery<T>(load: (signal: AbortSignal) => Promise<T>, key: string) {
  const [state, setState] = useState<QueryState<T>>({ key: null, data: null, error: null });
  const [nonce, setNonce] = useState(0);
  const loadRef = useRef(load);
  const requestKey = `${key}#${nonce}`;

  useEffect(() => {
    loadRef.current = load;
  });

  useEffect(() => {
    const controller = new AbortController();

    loadRef
      .current(controller.signal)
      .then((data) => setState({ key: requestKey, data, error: null }))
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) {
          setState((previous) => ({
            key: requestKey,
            data: previous.data,
            error: errorMessage(cause),
          }));
        }
      });

    return () => controller.abort();
  }, [requestKey]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  return { data: state.data, error: state.error, loading: state.key !== requestKey, reload };
}
